<template>
  <div class="dateRangePicker">
    <div v-if="mode === 'quick'" class="quickRanges">
      <div class="quickOptions">
        <div class="quickOption" v-if="isQuickOptionAvailable('last1')" @click="handleQuickRangeClick('last1')" :class="{selected: 'last1' === selectedQuickOption}">Yesterday</div>
        <div class="quickOption" v-if="isQuickOptionAvailable('last7')" @click="handleQuickRangeClick('last7')" :class="{selected: 'last7' === selectedQuickOption}">Last 7 Days</div>
        <div class="quickOption" v-if="isQuickOptionAvailable('last30')" @click="handleQuickRangeClick('last30')" :class="{selected: 'last30' === selectedQuickOption}">Last 30 Days</div>
        <div class="quickOption" v-if="isQuickOptionAvailable('last90')" @click="handleQuickRangeClick('last90')" :class="{selected: 'last90' === selectedQuickOption}">Last 90 Days</div>
        <div class="quickOption" v-if="isQuickOptionAvailable('thisMonth')" @click="handleQuickRangeClick('thisMonth')" :class="{selected: 'thisMonth' === selectedQuickOption}">This Month</div>
        <div class="quickOption" v-if="isQuickOptionAvailable('prevMonth')" @click="handleQuickRangeClick('prevMonth')" :class="{selected: 'prevMonth' === selectedQuickOption}">Previous Month</div>
        <div class="quickOption" v-if="isQuickOptionAvailable('last3m')" @click="handleQuickRangeClick('last3m')" :class="{selected: 'last3m' === selectedQuickOption}">Last 3 Months</div>
        <div class="quickOption" v-if="isQuickOptionAvailable('last12m')" @click="handleQuickRangeClick('last12m')" :class="{selected: 'last12m' === selectedQuickOption}">Last 12 Months</div>
        <div class="quickOption" v-if="isQuickOptionAvailable('weekTD')" @click="handleQuickRangeClick('weekTD')" :class="{selected: 'weekTD' === selectedQuickOption}">Week to Date</div>
        <div class="quickOption" v-if="isQuickOptionAvailable('monthTD')" @click="handleQuickRangeClick('monthTD')" :class="{selected: 'monthTD' === selectedQuickOption}">Month to Date</div>
        <div class="quickOption" v-if="isQuickOptionAvailable('quarterTD')" @click="handleQuickRangeClick('quarterTD')" :class="{selected: 'quarterTD' === selectedQuickOption}">Quarter to Date</div>
        <div class="quickOption" v-if="!isGov && isQuickOptionAvailable('yearTD')" @click="handleQuickRangeClick('yearTD')" :class="{selected: 'yearTD' === selectedQuickOption}">Year to Date</div>
        <div class="quickOption" v-if="isGov && isQuickOptionAvailable('ffyTD')" @click="handleQuickRangeClick('ffyTD')" :class="{selected: 'ffyTD' === selectedQuickOption}">Federal Fiscal Year to Date</div>
        <div class="quickOption" v-if="internalUser && isQuickOptionAvailable('last10y')" @click="handleQuickRangeClick('last10y')" :class="{selected: 'last10y' === selectedQuickOption}">Last 10 Years</div>
      </div>
      <button class="btn btn-primary" @click="goToCustomMode()">Custom Range</button>
    </div>
    <div v-if="mode === 'custom'"  class="customRange">
      <vc-date-picker
        v-model="dateRange"
        is-range
        color="purple"
        :columns="$screens({ default: 1, md: 2 })"
        :rows="$screens({ default: 1, md: 2 })"
        :is-expanded="$screens({ default: true, md: false })"
        :min-date="minDate"
        :max-date="maxDate"
      ></vc-date-picker>
      <div class="customRangeFooter">
        <span class="backToQuickLink" @click="goToQuickMode()">View other date ranges</span>
        <button class="btn btn-primary apply" @click="rangeSelected(dateRange.start, dateRange.end)">Apply</button>
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment-timezone';
  import DataProvider from "../../../app/data-framework/data-provider";
  let dateRangePicker = {
    name: 'DateRangePicker',
    props: {
      value: {
        type: Object,
        default: function() {
          return {
            startDate: undefined,
            endDate: undefined
          };
        },
      },
      minDate: {
        type: Date,
        default: function() {
          return undefined;
        }
      },
      maxDate: {
        type: Date,
        default: function() {
          return undefined;
        }
      }
    },
    watch: {
      value: function(value) {
          let startDate = value.startDate ? moment(value.startDate).toDate() : undefined;
          let endDate = value.endDate ? moment(value.endDate).toDate() : undefined;
          this.dateRange = {
            start: startDate,
            end: endDate
          };
        },
    },
    data: function () {
      let startDate = this.value.startDate ? moment(this.value.startDate).toDate() : undefined;
      let endDate = this.value.endDate ? moment(this.value.endDate).toDate() : undefined;
      return {
        dateRange: {
          start: startDate,
          end: endDate,
        },
        mode: 'quick',
        selectedQuickOption: '',
        internalUser: DataProvider.getInstance().isInternalUser(),
        isGov: window.Config && window.Config.isGov,
      };
    },
    methods: {
      goToQuickMode: function() {
        this.mode = 'quick';
      },
      goToCustomMode: function() {
        this.mode = 'custom';
      },
      isQuickOptionAvailable: function(rangeKey) {
        let min = this.minDate;

        if (!min) {
          return true;
        }

        switch (rangeKey) {
          case 'last1':
            return moment(min).isSameOrBefore(moment().subtract(1, 'days'));
          case 'last7':
            return moment(min).isSameOrBefore(moment().subtract(7, 'days'));
          case 'last30':
            return moment(min).isSameOrBefore(moment().subtract(30, 'days'));
          case 'last90':
            return moment(min).isSameOrBefore(moment().subtract(90, 'days'));
          case 'thisMonth':
            return moment(min).isSameOrBefore(moment().startOf('month'));
          case 'prevMonth':
            return moment(min).isSameOrBefore(moment().subtract(1, 'month').startOf('month'));
          case 'last3m':
            return moment(min).isSameOrBefore(moment().subtract(3, 'month').startOf('month'));
          case 'last12m':
            return moment(min).isSameOrBefore(moment().subtract(12, 'month').startOf('month'));
          case 'weekTD':
            return moment(min).isSameOrBefore(moment().subtract(1, 'days').startOf('isoWeek'));
          case 'monthTD':
            return moment(min).isSameOrBefore(moment().subtract(1, 'days').startOf('month'));
          case 'quarterTD':
            return moment(min).isSameOrBefore(moment().subtract(1, 'days').startOf('quarter'));
          case 'yearTD':
            return moment(min).isSameOrBefore(moment().subtract(1, 'days').startOf('year'));
          case 'ffyTD':
            // Federal Fiscal Year starts October 1
            let ffyStart = moment().month() >= 9 ? moment().month(9).date(1) : moment().subtract(1, 'year').month(9).date(1);
            return moment(min).isSameOrBefore(ffyStart);
          case 'last10y':
            return moment(min).isSameOrBefore(moment().subtract(10, 'year').startOf('month'));
          default: return false; //invalid key
        }
      },
      /**
       * Handles a click on the quick range picker.
       * 
       * @listens onclick, element
       */
      handleQuickRangeClick: function(rangeKey) {
        let startDate, endDate;

        switch (rangeKey) {
          case 'last1': 
            startDate = moment().subtract(1, 'days');
            endDate = moment().subtract(1, 'days');
            break;
          case 'last7': 
            startDate = moment().subtract(7, 'days');
            endDate = moment().subtract(1, 'days');
            break;
          case 'last30': 
            startDate = moment().subtract(30, 'days');
            endDate = moment().subtract(1, 'days');
            break;
          case 'last90': 
            startDate = moment().subtract(90, 'days');
            endDate = moment().subtract(1, 'days');
            break;
          case 'thisMonth':
            startDate = moment().startOf('month');
            endDate = moment().subtract(1, 'days');
            break;
          case 'prevMonth': 
            startDate = moment().subtract(1, 'month').startOf('month');
            endDate = moment().subtract(1, 'month').endOf('month');
            break;
          case 'last3m': 
            startDate = moment().subtract(3, 'month').startOf('month');
            endDate = moment().subtract(1, 'month').endOf('month');
            break;
          case 'last12m': 
            startDate = moment().subtract(12, 'month').startOf('month');
            endDate = moment().subtract(1, 'month').endOf('month');
            break;
          case 'weekTD': 
            startDate = moment().subtract(1, 'days').startOf('isoWeek');
            endDate = moment().subtract(1, 'days');
            break;
          case 'monthTD': 
            startDate = moment().subtract(1, 'days').startOf('month');
            endDate = moment().subtract(1, 'days');
            break;
          case 'quarterTD': 
            startDate = moment().subtract(1, 'days').startOf('quarter');
            endDate = moment().subtract(1, 'days');
            break;
          case 'yearTD': 
            startDate = moment().subtract(1, 'days').startOf('year');
            endDate = moment().subtract(1, 'days');
            break;
          case 'ffyTD':
            // Federal Fiscal Year starts October 1
            // If current month is October (9) or later, FFY started this year
            // Otherwise, FFY started last year
            if (moment().month() >= 9) {
              startDate = moment().month(9).date(1); // October 1 this year
            } else {
              startDate = moment().subtract(1, 'year').month(9).date(1); // October 1 last year
            }
            endDate = moment().subtract(1, 'days');
            break;
          case 'last10y':
            startDate = moment().subtract(10, 'year').startOf('month');
            endDate = moment().subtract(1, 'days');
            break;
          default: return; //invalid key, prevent range selection
        }
        this.rangeSelected(startDate, endDate);
        this.selectedQuickOption = rangeKey;
      },
      rangeSelected: function(start, end) {
        this.selectedQuickOption = '';
        this.$emit('range-selected', {
          startDate: start,
          endDate: end
        });
      }
    }
  };
  export default dateRangePicker;
</script>

<style lang="scss" scope>
$hoverColor: #ECF0F5;
$selectedColor: var(--default-color);
.dateRangePicker {
  display: inline-block;
  .vc-container {
    font-family: var(--font-family);
  }
  .quickRanges {
    padding: 5px 0;
    .quickOptions {
      margin-bottom: 5px;
      .quickOption {
        padding: 5px;
        text-align: center;
        cursor: pointer;
        &:hover {
          background-color: $hoverColor;
        }
        &.selected {
          background-color: $selectedColor;
          color: white;
        }
      }
    }
    .btn {
      width: 100%;
    }
  }
  .customRange {
    padding: 10px;
    .vc-container {
      border: none;
    }
    .customRangeFooter {
      width: 500px;
      .backToQuickLink {
        color: var(--default-color);
        width: calc(50% - 10px);
        text-align: center;
        display: inline-block;
        cursor: pointer;
        &:hover {
          text-decoration: underline;
        }
      }
      .btn {
        width: calc(50% - 30px);
        margin-left: 30px;
      }
    }
  }
}
</style>
